import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { doc, onSnapshot } from 'firebase/firestore'
import {
  byCarNumber,
  canonicalShareToken,
  chooseFor,
  describeFreshness,
  isKnownScreen,
  neededDataForScreens,
  orderSessions,
  parseScreens,
} from '@racedash/shared'
import type { SharedViewDoc } from '@racedash/shared'
import { db } from '../lib/firebase'
import ScreenView from '../dashboard/ScreenView'
import ScreenSwitcher from '../dashboard/ScreenSwitcher'
import { useEventSessions } from '../dashboard/useDashboardSource'
import { noContext } from '../dashboard/screenContext'
import { useNow, useSessionData } from '../dashboard/useSessionData'
import { useAnonymousIdentity } from '../sharing/useAnonymousIdentity'
import { useLocalSetting } from '../lib/useLocalSetting'
import '../dashboard/dashboard.css'

/**
 * The public race view. No account, no sign-up, and deliberately outside the
 * app shell — this route must never be able to reach organisation data, and the
 * cleanest way to guarantee that is for it to have no code path that could.
 *
 * It reads exactly one document of its own: sharedViews/{token}. Holding the
 * token is the proof of entitlement, since the token is the path.
 *
 * Everything else it reads is derived timing, which the design doc calls what it
 * is — facts about a public race. It reaches those with an anonymous identity
 * rather than by widening any rule, so every membership check keeps failing and
 * the organisation's own overlay stays private.
 */
export default function SharedView() {
  const { token: typed } = useParams()
  // A link read out and typed in arrives as "Donkey Butter Kite" as often as
  // donkey-butter-kite. Both mean the same link, and the address bar is put
  // right so what gets bookmarked or passed on is the tidy one.
  const token = typed ? canonicalShareToken(typed) : typed
  const navigate = useNavigate()
  useEffect(() => {
    if (typed && token && token !== typed) {
      navigate({ pathname: `/s/${token}`, search: window.location.search }, { replace: true })
    }
  }, [typed, token, navigate])
  const [params, setParams] = useSearchParams()
  const [view, setView] = useState<SharedViewDoc | null | undefined>(undefined)
  const identity = useAnonymousIdentity()
  const now = useNow()

  useEffect(() => {
    if (!token) return
    return onSnapshot(
      doc(db, 'sharedViews', token),
      (snap) => setView(snap.exists() ? (snap.data() as SharedViewDoc) : null),
      () => setView(null),
    )
  }, [token])

  const screens = useMemo(
    () => (view ? parseScreens(view.screens, view.layout) : []),
    [view],
  )
  /**
   * What this link may see, resolved server-side when it was minted.
   *
   * The viewer holds no organisation, so the entries are projected onto the
   * shared view itself — and then filtered by exactly the same code a member's
   * dashboard runs, rather than a second implementation that could disagree
   * about which sessions are theirs.
   */
  const scope = view?.scope ?? null

  // The car rides in the URL, so one link can follow whichever car the person
  // sharing it meant — the same parameterisation a member's dashboard has, and
  // falling back the same way to whichever car the dashboard is about.
  const car = params.get('car') ?? view?.defaultCar ?? null
  /*
   * The session rides there too, and for a second reason.
   *
   * A viewer changing it is one thing. The other is that a shared link is
   * pasted into a message and opened by several people at once: with the choice
   * in the URL, the person sharing can send everybody to the same session
   * rather than to whatever each recipient's browser decides is freshest.
   */
  const pickedSessionId = params.get('session')
  const [activeId, setActiveId] = useState<string | null>(null)
  const stageRef = useRef<HTMLDivElement | null>(null)
  const active = screens.find((s) => s.id === activeId) ?? screens[0] ?? null

  /*
   * Filling the browser window, remembered per link.
   *
   * The reason to turn it on is usually that this link is open on a screen
   * somebody walks past, and having to set it again after every reload would
   * defeat the point. Escape gets out and the button stays visible, so it is
   * not a state anybody can be trapped in.
   */
  const [filling, setFilling] = useLocalSetting<boolean>(
    `racedash.shared.${token ?? 'anon'}.filling`,
    false,
    (raw) => raw === true,
  )

  useEffect(() => {
    if (!filling) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFilling(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [filling, setFilling])

  /** Change one URL parameter, leaving the rest of the link alone. */
  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params)
      if (value) next.set(key, value)
      else next.delete(key)
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  /*
   * Nothing is read until the identity exists.
   *
   * The two halves of a share link are entitled differently. sharedViews is
   * world-readable, because holding the token is the proof; the timing it
   * points at sits behind signedIn(), so that a leaked link cannot become a
   * read key for the whole database. An anonymous sign-in bridges them.
   *
   * On a first visit with nothing cached the view arrives before the sign-in
   * does, and a subscription opened in that window is refused — permanently,
   * because a rejected listener is not retried when the token turns up a moment
   * later. The page then said "Timing could not be read for this event" for the
   * rest of its life, which was true and sounded like a fault in the data.
   *
   * A member never saw it: their token is already cached, so the window is
   * empty. Only the first incognito visit hits it, which is every recipient of
   * a link.
   */
  const ready = identity.status === 'ready'
  const {
    sessions,
    loading: sessionsLoading,
    error: sessionsError,
  } = useEventSessions(ready ? (scope?.eventId ?? null) : null)

  const { session, ours, freshness } = useMemo(() => {
    const entries = (scope?.entries ?? []).map((e) => ({
      number: e.number,
      series: e.series ?? undefined,
      seriesMatched: e.seriesMatched ?? undefined,
      channel: e.channel ?? null,
    }))
    // Falls back rather than failing when the URL names a session that no
    // longer resolves. A shared link is bookmarked and reopened weeks later,
    // when "nothing captured" is a worse answer than this weekend's race — the
    // one place this deliberately differs from a member's dashboard.
    const { ours, session } = chooseFor(entries, sessions, {
      nowMs: now.getTime(),
      preferCar: car,
      picked: pickedSessionId,
      unresolved: 'fallback',
    })
    return { session, ours, freshness: describeFreshness(session, now.getTime()) }
  }, [scope, sessions, now, car, pickedSessionId])

  /*
   * Which sessions to offer: the team's own, on the channels the event's
   * administrator set. Nothing else reaches a link holder.
   */
  const sessionOptions = useMemo(
    () =>
      orderSessions(ours).map((s) => ({
        id: s.id,
        label: [s.seriesNameRaw, s.sessionNameRaw].filter(Boolean).join(' — ') || s.id,
      })),
    [ours],
  )

  /*
   * Which cars to offer: the team's first, then everybody else on the timing.
   *
   * The team's own entries come from the link's scope, which is what the link
   * is about — following one of their cars rather than another is the change a
   * recipient actually wants. The rest are offered underneath because the
   * timing is public either way, and a viewer who wants to watch whoever the
   * team is racing should not need a second link.
   *
   * Taken from the session's own accumulated car list rather than the
   * standings, which a dashboard only loads when one of its screens asks for
   * them — a link showing nothing but a lap chart would otherwise have no cars
   * to offer.
   */
  const carOptions = useMemo(() => {
    const mine = (scope?.entries ?? []).map((e) => e.number).filter(Boolean)
    const set = new Set(mine)
    const rest = (session?.cars ?? []).filter((n) => !set.has(n)).sort(byCarNumber)
    return { mine: [...mine].sort(byCarNumber), rest }
  }, [scope, session])

  const needs = useMemo(() => neededDataForScreens(screens), [screens])
  // Gated on the identity too. It is already gated behind the sessions query in
  // practice, but relying on that would mean this stayed correct only for as
  // long as nobody chose a session by any other route.
  const data = useSessionData(ready ? (session?.id ?? null) : null, car, needs)

  if (view === undefined || identity.status === 'signing-in') {
    return (
      <div className="centre">
        <p className="muted">Loading…</p>
      </div>
    )
  }

  if (view === null) {
    return (
      <Frame>
        <h1 className="panel-title">This link isn't available</h1>
        <p className="muted">
          It may have been revoked by whoever shared it, or the dashboard may have been deleted.
          Ask them for a new link.
        </p>
      </Frame>
    )
  }

  if (identity.status === 'unavailable') {
    return (
      <Frame>
        <h1 className="panel-title">Live timing isn't available here</h1>
        <p className="muted">
          This browser could not get a session to read timing data with. Anonymous sign-in may be
          switched off for RaceDash, which is a setting only its administrator can change.
        </p>
        <p className="muted small">{identity.reason}</p>
      </Frame>
    )
  }

  return (
    <div className="shell">
      <header className="appbar">
        <a className="wordmark" href="https://racedash.live">
          RaceDash<span>.</span>live
        </a>
        <span className="chip">Shared view</span>
      </header>

      <main className="content">
        {/* The fill-window layer is its own element rather than the <main>.
            .content caps itself at 60rem, and a fixed layer wearing that class
            is fixed to the viewport and then trimmed to 960px of it — which
            filled neither the window nor the page. */}
        <div className="shared-main dash-host" data-filling={filling || undefined}>
          <div className="page-head">
            <div>
              <h1 className="page-title">{view.name}</h1>
              <p className="muted small">
                {session
                  ? [session.meetingNameRaw, session.sessionNameRaw].filter(Boolean).join(' — ')
                  : 'Waiting for timing'}
                {session && ` · ${freshness.label}`}
              </p>
            </div>
            <div className="shared-actions">
              {/* Native controls rather than the component library's, for the
                  reason the tabs below are: this route is deliberately outside
                  the app shell, and reaching back across that line for two
                  dropdowns would buy nothing. */}
              {sessionOptions.length > 1 && (
                <select
                  className="shared-select"
                  aria-label="Session"
                  value={session?.id ?? ''}
                  onChange={(e) => setParam('session', e.target.value || null)}
                >
                  {sessionOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              )}

              {(carOptions.mine.length > 0 || carOptions.rest.length > 0) && (
                <select
                  className="shared-select"
                  aria-label="Car"
                  value={car ?? ''}
                  onChange={(e) => setParam('car', e.target.value || null)}
                >
                  <option value="">All cars</option>
                  {/* Grouped only when there is something to distinguish. A link
                      shared by a one-car team would otherwise get a heading over
                      a list of one. */}
                  {carOptions.rest.length > 0 ? (
                    <>
                      {carOptions.mine.length > 0 && (
                        <optgroup label="This team">
                          {carOptions.mine.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label="Everyone else">
                        {carOptions.rest.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </optgroup>
                    </>
                  ) : (
                    carOptions.mine.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))
                  )}
                </select>
              )}

              {/*
                Two different things, offered separately for the same reason the
                member dashboard offers both.

                "Fill window" keeps the browser's own chrome — the tab, the
                address bar, and the URL that says which link this is. That is
                what you want on a machine you are also using for something else.
                "Full screen" hands the display over entirely, which is right for
                a screen bolted to a wall and wrong for a laptop on a desk.
              */}
              <button type="button" className="shared-tab" onClick={() => setFilling(!filling)}>
                {filling ? 'Exit' : 'Fill window'}
              </button>
              <button
                type="button"
                className="shared-tab"
                onClick={() => void stageRef.current?.requestFullscreen?.()}
              >
                Full screen
              </button>
            </div>
          </div>

          {screens.length > 1 && (
            <nav className="shared-tabs">
              {screens.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="shared-tab"
                  data-active={s.id === active?.id || undefined}
                  onClick={() => setActiveId(s.id)}
                >
                  {s.name}
                  {!isKnownScreen(s) && ' ⚠'}
                </button>
              ))}
            </nav>
          )}

          {screens.length === 0 ? (
            <div className="dash-frame dash-frame--idle">
              <p className="muted">This dashboard has no screens yet.</p>
            </div>
          ) : !session ? (
            <div className="dash-frame dash-frame--idle">
              {/* Nothing yet and nothing working must not read the same. A link
                  opened on the Thursday before a race is the common case, and
                  telling somebody it is broken when it is merely early is the
                  fastest way to be asked whether it is broken. */}
              <p className="muted">
                {waitingReason(scope, sessions.length, sessionsError, sessionsLoading, now)}
              </p>
            </div>
          ) : (
            <div className="dash-frame" ref={stageRef}>
              {/* A public viewer full-screening a shared link has the same
                  problem a member does: the tabs are outside the stage. */}
              <ScreenSwitcher
                screens={screens}
                activeId={active?.id ?? null}
                onSelect={setActiveId}
                stageRef={stageRef}
              />
              {active && (
                <ScreenView
                  screen={active}
                  // A public viewer never edits, which is also why the editor is
                  // loaded lazily and never reaches this bundle.
                  editing={false}
                  onChange={() => {}}
                  data={data}
                  primaryCar={car}
                  now={now}
                  // Timing and nothing else. A link holder has no organisation,
                  // so they see the vendor's driver names rather than the team's
                  // — the declarations are private to members by rule, and that
                  // stays true.
                  context={noContext(
                    view.dashboardId,
                    (scope?.entries ?? []).map((e) => e.number).filter(Boolean),
                  )}
                />
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

/**
 * Why a shared view is empty.
 *
 * A link opened on the Thursday before a race is the ordinary case, and it must
 * not read like a fault — telling somebody a page is broken when it is merely
 * early is the quickest way to be asked whether it is broken.
 */
function waitingReason(
  scope: SharedViewDoc['scope'] | null,
  captured: number,
  error: string | null,
  loading: boolean,
  now: Date,
): string {
  if (!scope?.eventId) {
    return "Whoever shared this hasn't pointed it at an event yet, so there is nothing to show."
  }
  if (error) return 'Timing could not be read for this event.'
  // Before the answer arrives, say nothing about it. Every line below is a
  // statement about what was found, and stating any of them while still looking
  // is the same mistake in miniature as calling an early link broken.
  if (loading) return 'Loading timing…'

  const name = scope.eventName ?? 'This event'
  const starts = scope.startsAt?.toDate()
  if (starts && now < starts) {
    const days = Math.ceil((starts.getTime() - now.getTime()) / 86_400_000)
    const when = days <= 1 ? 'tomorrow' : `in ${days} days`
    return `${name} starts ${when}. This page will fill in once the cars are on track.`
  }
  if (captured === 0) {
    return `Nothing has been captured for ${name} yet. This page will fill in once the cars are on track.`
  }
  return `Timing is being recorded at ${name}, but none of it is this team's yet.`
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="centre">
      <div className="panel">
        <a className="wordmark" href="https://racedash.live">
          RaceDash<span>.</span>live
        </a>
        {children}
      </div>
    </div>
  )
}
